import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { Badge } from "../../components/Badge";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import {INVOICES_LIST_QUERY} from "./queries";
import { organizationStore } from "../../store/organization";
import { useEffect, useRef, useState } from "react";
import { eventStore } from "../../store/event";
import moment from "moment/moment";
import { usePermissions } from "../auth/api";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import {Invoice} from "./type";
import {usdFormat} from "../../utils";
import Switch from "react-switch";

const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getEventStore } = eventStore;
const buildSearch = (search: string) => [
  {
    invoice_number: {
      _ilike: `%${search}%`,
    },
  },
  {
    bill_to_name: {
      _ilike: `%${search}%`,
    },
  },
  {
    bill_to_email: {
      _ilike: `%${search}%`,
    },
  },
  {
    from_name: {
      _ilike: `%${search}%`,
    },
  },
  {
    from_email: {
      _ilike: `%${search}%`,
    },
  },
  {
    status: {
      _ilike: `%${search}%`,
    },
  },
];

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: Invoice;
}) => JSX.Element | null;
const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: Invoice }) => string
): CellRenderer => {
  return (params) => {
    if (params.value === undefined) {
      return null;
    }

    return (
      <>
        {permission ? (
          <Link to={linkFunc(params)}>
            <Badge>{params.value}</Badge>
          </Link>
        ) : (
          <Badge>{params.value}</Badge>
        )}
      </>
    );
  };
};

export const invoicesListLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const search = url.searchParams.get("search");
  const organizationId = getState().organizationId;
  const eventId = getEventStore().eventId;

  let where: any = { _or: search ? buildSearch(search) : undefined };
  if (organizationId) {
    where = {
      ...where,
      organization_id: {
        _eq: organizationId,
      },
    };
  }
  if (eventId) {
    where = {
      ...where,
      event_id: {
        _eq: eventId,
      },
    };
  }
  const { data } = await client.query({
    query: INVOICES_LIST_QUERY,
    variables: {
      where,
    },
  });
  return data.invoices;
};

const formatPercent = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "";
  if (value === MIXED_RATES) return MIXED_RATES;
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : "";
};

const MIXED_RATES = "Mixed";

// Line-item invoices keep their tax / service fee % on each line, not on the
// invoice row (those columns save as 0 or null for this type). Read the rate
// from the lines: one shared rate -> that rate; different rates -> "Mixed".
// Returns undefined for "total" invoices so the invoice-level value is used.
const lineItemPercent = (
  invoice: Invoice,
  key: "taxPercentage" | "serviceFeePercentage"
): number | string | null | undefined => {
  if (invoice.invoice_type !== "line_item") return undefined;
  try {
    const lines = JSON.parse(invoice.invoice_line_items ?? "[]");
    if (!Array.isArray(lines) || lines.length === 0) return null;
    const rates = [...new Set(lines.map((line: any) => Number(line?.[key] ?? 0)))];
    return rates.length === 1 ? rates[0] : MIXED_RATES;
  } catch {
    return null;
  }
};

const serviceFeePercent = (invoice?: Invoice) => {
  if (!invoice) return null;
  const fromLines = lineItemPercent(invoice, "serviceFeePercentage");
  return fromLines !== undefined ? fromLines : invoice.service_fee_percentage;
};

const taxPercent = (invoice?: Invoice) => {
  if (!invoice) return null;
  const fromLines = lineItemPercent(invoice, "taxPercentage");
  return fromLines !== undefined ? fromLines : invoice.tax_percentage;
};

export const InvoicesList = () => {
  const { editCreatePermission, ListPermission, moduleWisePermission } =
    usePermissions("invoices");
  const { editCreatePermission: eventsProcessorEditPermission } =
    usePermissions("events");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const invoices = useLoaderData() as Invoice[];
  const [rowData, setRowData] = useState<Invoice[]>([]);
  const [isActiveToggle, setIsActiveToggle] = useState(true);
  useEffect(() => {
    const filteredItems = isActiveToggle
        ? invoices.filter((invoice) => invoice.is_active)
        : invoices;
    setRowData(filteredItems);
  }, [invoices, isActiveToggle]);
  const generateColDefs = (): ColDef<Invoice>[] => [
    {
      headerName: "Id",
      valueGetter: (p) => p.data?.id,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/invoices/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Invoice Number",
      valueGetter: (p) => p.data?.invoice_number,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/invoices/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Status",
      valueGetter: (p) => p.data?.status,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "To Name",
      valueGetter: (p) => p.data?.bill_to_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "To Email",
      valueGetter: (p) => p.data?.bill_to_email,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Invoice Grand Total",
      valueGetter: (p) => p.data?.grand_total_amount,
      valueFormatter: (params) => usdFormat.format((params.value ?? 0)),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => usdFormat.format((p.data?.grand_total_amount ?? 0)),
    },
    // Stored as percent values (3.5 = 3.5%), null on older invoices.
    // Line-item invoices read the rate from their lines (RP-2546).
    {
      headerName: "Service Fee %",
      valueGetter: (p) => serviceFeePercent(p.data),
      valueFormatter: (p) => formatPercent(p.value),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => formatPercent(serviceFeePercent(p.data)),
    },
    {
      headerName: "Tax %",
      valueGetter: (p) => taxPercent(p.data),
      valueFormatter: (p) => formatPercent(p.value),
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => formatPercent(taxPercent(p.data)),
    },
    {
      headerName: "Invoice Date",
      valueGetter: (p) => p.data?.invoice_date,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYY") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.invoice_date ? moment(p.data.invoice_date).format("MM/DD/YYY") : "",
    },
    {
      headerName: "Invoice Due Date",
      valueGetter: (p) => p.data?.invoice_due_date,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.invoice_due_date ? moment(p.data.invoice_due_date).format("MM/DD/YYYY") : "",
    },
    {
      headerName: "From Name",
      valueGetter: (p) => p.data?.from_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "From Email",
      valueGetter: (p) => p.data?.from_email,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Event",
      valueGetter: (p) => p.data?.invoice_event?.name,
      cellRenderer: createCellRenderer(
        eventsProcessorEditPermission,
        (p) => `/events/${p.data.event_id}/edit`
      ),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Updated By",
      valueGetter: (p) => p.data?.invoice_user?.username,
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
  ];
  const colDefs: ColDef<Invoice>[] = generateColDefs();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `Invoices_${timestamp}.csv`,
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <div style={{ height: "75vh" }}>
      <div
        className={"ag-theme-quartz"}
        style={{ width: "100%", height: "100%" }}
      >
        <div className=" grid gap-2 grid-cols-6">
          <Breadcrumbs />
          <div className="col-span-1 col-start-4 mt-3 flex flex-col items-center">
            <span className="block text-sm font-medium text-gray-500">Active Only</span>
            <Switch
                checked={isActiveToggle}
                onChange={() => setIsActiveToggle((prev) => !prev)}
                onColor="#EE2E30"
                onHandleColor="#FFFFFF"
                handleDiameter={18}
                uncheckedIcon={false}
                checkedIcon={false}
                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                height={15}
                width={48}
            />
          </div>
          <div className="col-span-2 col-start-5 mt-2">
            <Input
              name="search"
              placeholder={`Search Invoices`}
              onChange={(s) => {
                navigate(
                  s && typeof s === "string" && s.length > 0
                    ? `/invoices/list?search=${s}`
                    : `/invoices/list`
                );
                revalidator.revalidate();
              }}
            />
          </div>
          <div className="py-2 col-span-2 col-start-7">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
            {editCreatePermission && (
              <PrimaryButton onClick={() => navigate(`/invoices/create`)}>
                Create
              </PrimaryButton>
            )}
          </div>
        </div>
        <AgGridReact
          rowData={rowData}
          columnDefs={colDefs}
          rowHeight={rowHeight}
          autoSizeStrategy={autoSizeStrategy}
          pagination={true}
          gridOptions={gridOptions}
          paginationPageSize={100}
          onGridReady={(params) => (gridRef.current = params.api)}
        />
      </div>
    </div>
  );
};
